import { Component, forwardRef, HostBinding, HostListener, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConditionalOption, DataType } from '@core/constants/data-types.constants';

@Component({
  selector: 'mfp-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ]
})
export class SelectComponent implements ControlValueAccessor {
  @Input() view = 'agent';
  @Input() color: 'primary' | 'light' = 'primary';
  @Input() label = '';
  @Input() name = '';
  @Input() placeholder = '';
  @Input() set options(options: ConditionalOption<DataType>[]) {
    this.innerOptions = options;
    const option = this.innerOptions.find(o => o.value === this.innerValue);
    this.selectValue = option || this.selectValue;
  }
  @Input() required = false;
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() success = false;
  @Input() error = false;
  @Input() helper = '';
  @HostBinding('tabindex') tabindex = 0;

  innerOptions: ConditionalOption<DataType>[] = [];
  showOptions = false;
  selectValue?: ConditionalOption<DataType>;

  private innerValue: DataType = '';
  private firstClick = true;

  @HostListener('focus')
  onFocus() {
    this.toggleOptions(true);
  }

  @HostListener('focusout')
  onFocusout() {
    this.toggleOptions(false);
    this.firstClick = true;
  }

  constructor() {}

  onClickSelect(value: boolean) {
    if (this.firstClick) {
      this.firstClick = false;
    } else {
      this.toggleOptions(value);
    }
  }

  toggleOptions(value: boolean): void {
    if (!this.disabled && !this.readonly) {
      this.showOptions = value;
    }
  }

  selectOption(option: ConditionalOption<DataType>): void {
    this.selectValue = option;
    this.value = option.value;
    this.toggleOptions(!this.showOptions);
  }

  // ControlValueAccessor
  private onChangeCallback = (_: DataType) => {};
  private onTouchCallback = () => {};

  get value(): DataType {
    return this.innerValue;
  }

  set value(value: DataType) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.onChangeCallback(value);
      this.onTouchCallback();
    }
  }

  writeValue(value: DataType): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
      const option = this.innerOptions.find(o => o.value === this.innerValue);
      this.selectValue = option;
    }
  }

  registerOnChange(fn: (_: DataType) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchCallback = fn;
  }

  setDisabledState(state: boolean): void {
    this.disabled = state;
  }
}
